import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store';

/**
 * Route guard for pages that require a signed-in user. Behaviour:
 *
 *   * Token absent → redirect to /login, preserving the original location in
 *     `location.state.from` so the login page can bounce the user back after success.
 *   * Token present but hydration not complete → render a thin loading sentinel rather than
 *     gambling on stale persisted state. Hydration takes <100 ms on a fast connection.
 *   * Token present + hydrated + user disabled → server already cleared the session in
 *     `hydrate()`, so this branch only fires during the window where `user` is briefly null.
 *     Redirect to /login.
 *
 * Used by wrapping the protected element:
 *
 *   <Route path="/security" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const token = useAuthStore((s) => s.token);
  const hydrated = useAuthStore((s) => s.hydrated);

  if (!hydrated) {
    // First-paint sentinel — Tailwind utility-only so we don't drag in another spinner asset.
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-[var(--color-text-muted)]">Loading…</p>
      </main>
    );
  }
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
